/**
 * FayreDetailPage — /fayres/:id (customer-facing).
 *
 * The conversion surface. Hero with fayre meta + aggregate reply-speed
 * trust signal, sticky category filter pills, and a sorted supplier
 * list reusing the trust chips we ship everywhere else (verified, reply
 * bucket, docs ready, booked N this week). A "Message Suppliers" bar
 * appears when ≥1 suppliers are selected — this is the multi-message
 * blast that turns fayre intent into live chats in one tap.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, MapPin, CalendarBlank, Users, Lightning, FileText, CheckCircle,
  Storefront, ChatCircle, X,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { ShareFayreButton, logFayreLanding } from '../components/ShareFayreButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_EMOJI = {
  dj: '🎧', photographer: '📷', videographer: '🎥',
  photobooth: '📸', caterer: '🍽️',
  florist: '🌼', venue: '🏛️', cake_maker: '🎂',
  transport: '🚗', decorator: '🎨',
  wedding_planner: '✨', hair_makeup: '💅',
};

const CATEGORY_LABEL = {
  dj: 'DJs', photographer: 'Photographers', videographer: 'Videographers',
  photobooth: 'Photo Booths', caterer: 'Catering',
  florist: 'Florists', venue: 'Venues', cake_maker: 'Cake Makers',
  transport: 'Transport', decorator: 'Decorators',
  wedding_planner: 'Planners', hair_makeup: 'Hair & Makeup',
};

const REPLY_BUCKET_SHORT = {
  within_15m: 'Replies within 15 mins',
  within_1h: 'Replies within 1 hour',
  same_day: 'Replies same day',
};

const TIER_META = {
  standard: null,
  featured: { label: 'Featured', cls: 'bg-amber-400/15 text-amber-200 border-amber-400/40' },
  premium: { label: '⭐ Premium', cls: 'bg-gradient-to-r from-[#B89762]/20 to-[#B89762]/20 text-[#F4E9D2] border-[#B89762]/45/40' },
};

const formatPrice = (n) => (typeof n === 'number' && n > 0 ? `£${Math.round(n).toLocaleString('en-GB')}` : null);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
  } catch {
    return iso;
  }
};

const FayreDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fayre, setFayre] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [category, setCategory] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [msgModalOpen, setMsgModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/fayres/${id}`),
      axios.get(`${API}/fayres/${id}/suppliers`),
    ])
      .then(([a, b]) => {
        if (cancelled) return;
        setFayre(a.data.fayre);
        setSuppliers(b.data.suppliers || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // Attribution — log the landing if this visit came from a
    // supplier's share link. Silent; never blocks render.
    logFayreLanding(id);
    return () => { cancelled = true; };
  }, [id]);

  const filteredSuppliers = useMemo(() => {
    if (category === 'all') return suppliers;
    return suppliers.filter((s) => s.category === category);
  }, [suppliers, category]);

  const availableCategories = useMemo(() => {
    const set = new Set();
    suppliers.forEach((s) => set.add(s.category));
    return Array.from(set);
  }, [suppliers]);

  const toggleSelect = (sid) => {
    setSelectedIds((prev) => {
      if (prev.includes(sid)) return prev.filter((x) => x !== sid);
      if (prev.length >= 5) {
        toast.error('You can message up to 5 suppliers at once');
        return prev;
      }
      return [...prev, sid];
    });
  };

  const openSingleChat = async (sid) => {
    if (!user) { toast('Sign in to message this supplier'); navigate('/login'); return; }
    try {
      const { data } = await axios.post(`${API}/chats`, {
        supplier_id: sid,
        source: 'fayre',
        source_ref: id,
      });
      navigate(`/chat/${data.chat_id}`);
    } catch {
      toast.error('Could not open chat');
    }
  };

  if (notFound) return (
    <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center p-8">
      <div className="text-center">
        <p className="font-bold text-lg mb-2">Fayre not found</p>
        <button onClick={() => navigate('/fayres')} className="text-[#C29D52] text-sm underline">
          Back to all fayres
        </button>
      </div>
    </div>
  );

  if (loading || !fayre) return (
    <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center" data-testid="fayre-loading">
      <p className="text-white/40 text-sm">Loading fayre…</p>
    </div>
  );

  const replyCopy = fayre.reply_speed_bucket ? REPLY_BUCKET_SHORT[fayre.reply_speed_bucket] : null;

  return (
    <div className="min-h-screen bg-[#0F1014] text-white pb-32" data-testid="fayre-detail-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-[#0F1014]/95 backdrop-blur border-b border-white/5 px-5 py-3 flex items-center gap-3">
        <button onClick={() => navigate('/fayres')} className="text-white/60 hover:text-white" data-testid="fayre-back-btn">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <p className="font-bold text-sm truncate flex-1">{fayre.name}</p>
      </div>

      {/* Hero */}
      <div className="max-w-4xl mx-auto px-5 pt-6">
        <h1 className="text-3xl md:text-4xl font-black leading-tight" data-testid="fayre-hero-name">
          {fayre.name}
        </h1>
        <p className="text-white/60 text-sm mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1">
            <MapPin size={12} weight="bold" /> {fayre.location.venue}{fayre.location.city ? ` · ${fayre.location.city}` : ''}
          </span>
          <span className="inline-flex items-center gap-1">
            <CalendarBlank size={12} weight="bold" /> {formatDate(fayre.date)}
            {fayre.start_time && ` · ${fayre.start_time}–${fayre.end_time}`}
          </span>
        </p>

        {/* Hero trust chips */}
        <div className="flex flex-wrap gap-2 mt-4" data-testid="fayre-hero-signals">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border border-emerald-400/40 bg-emerald-400/15 text-emerald-200">
            <Users size={12} weight="fill" /> {fayre.supplier_count} suppliers attending
          </span>
          {replyCopy && (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border border-[#B89762]/45/30 bg-[#B89762]/40/10 text-[#C29D52]/85"
              data-testid="fayre-reply-signal"
            >
              <Lightning size={12} weight="fill" /> {replyCopy}
            </span>
          )}
          {fayre.activity_last_hour > 0 && (
            <span
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black border border-amber-400/30 bg-amber-400/10 text-amber-200"
              data-testid="fayre-activity-signal"
              title="Live — couples actively messaging suppliers right now"
            >
              🔥 {fayre.activity_last_hour} {fayre.activity_last_hour === 1 ? 'couple' : 'couples'} messaged suppliers in the last hour
            </span>
          )}
          <ShareFayreButton
            fayreId={fayre.id}
            fayreName={fayre.name}
            variant="inline"
            data-testid="fayre-hero-share-btn"
          />
        </div>

        {fayre.description && (
          <p className="text-white/70 text-sm mt-4 leading-relaxed" data-testid="fayre-description">
            {fayre.description}
          </p>
        )}
      </div>

      {/* Category filter pills */}
      <div className="sticky top-[52px] z-10 bg-[#0F1014]/95 backdrop-blur border-b border-white/5 mt-6">
        <div className="max-w-4xl mx-auto px-5 py-3 overflow-x-auto">
          <div className="flex gap-2" data-testid="fayre-category-pills">
            <CategoryPill active={category === 'all'} onClick={() => setCategory('all')} label="All" testid="fayre-cat-all" />
            {availableCategories.map((c) => (
              <CategoryPill
                key={c}
                active={category === c}
                onClick={() => setCategory(c)}
                label={`${CATEGORY_EMOJI[c] || '🎯'} ${CATEGORY_LABEL[c] || c}`}
                testid={`fayre-cat-${c}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Supplier list */}
      <div className="max-w-4xl mx-auto px-5 py-6">
        {!filteredSuppliers.length && (
          <div className="text-center py-16 rounded-2xl bg-white/[0.02] border border-white/5" data-testid="fayre-suppliers-empty">
            <Storefront size={32} weight="fill" className="mx-auto mb-2 text-white/20" />
            <p className="font-bold text-white/70">No suppliers in this category yet</p>
            <p className="text-white/40 text-sm mt-1">Try "All" or check back closer to the event.</p>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {filteredSuppliers.map((s) => (
            <FayreSupplierCard
              key={s.id}
              s={s}
              selected={selectedIds.includes(s.id)}
              onToggleSelect={() => toggleSelect(s.id)}
              onMessage={() => openSingleChat(s.id)}
              onView={() => navigate(`/suppliers/${s.id}`)}
            />
          ))}
        </div>
      </div>

      {/* Sticky multi-message bar (shown when ≥1 selected) */}
      {selectedIds.length > 0 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-30 border-t border-white/10 bg-[#0F1014]/95 backdrop-blur p-3"
          data-testid="fayre-multimessage-bar"
        >
          <div className="max-w-4xl mx-auto flex items-center gap-3">
            <p className="text-sm text-white/80 font-bold">
              {selectedIds.length} supplier{selectedIds.length === 1 ? '' : 's'} selected
            </p>
            <div className="flex-1" />
            <button
              onClick={() => setSelectedIds([])}
              className="px-3 py-2 rounded-lg text-[11px] font-bold text-white/60 hover:text-white"
              data-testid="fayre-clear-selection"
            >
              Clear
            </button>
            <button
              onClick={() => {
                if (!user) { toast('Sign in to message'); navigate('/login'); return; }
                setMsgModalOpen(true);
              }}
              className="px-4 py-2.5 rounded-xl text-sm font-bold text-white flex items-center gap-2"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="fayre-open-multimsg-btn"
            >
              <ChatCircle size={14} weight="fill" /> Message {selectedIds.length}
            </button>
          </div>
        </div>
      )}

      {/* Why attend + Share — emotional reinforcement at the bottom of
         the page, just before the final CTA. Short, no fluff. */}
      <div className="max-w-4xl mx-auto px-5 mt-4">
        <div className="rounded-2xl p-5 bg-white/[0.02] border border-white/5" data-testid="fayre-why-attend">
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#C29D52]/80 mb-2">Why attend this fayre</p>
          <ul className="space-y-1.5 text-sm text-white/75">
            <li className="flex items-center gap-2">✅ Meet suppliers in person before booking</li>
            <li className="flex items-center gap-2">✅ Compare options in one place</li>
            <li className="flex items-center gap-2">✅ Get quotes before the day</li>
          </ul>
        </div>

        {/* Share loop — viral expansion. Pass-through to ShareFayreButton
           which already handles share counts + attribution telemetry. */}
        <div className="mt-4 rounded-2xl p-5 bg-gradient-to-br from-[#B89762]/10 to-[#B89762]/8 border border-[#B89762]/40/15 text-center" data-testid="fayre-share-card">
          <p className="text-base font-black text-white">Planning with friends?</p>
          <p className="text-white/55 text-xs mt-1 mb-3">Send this fayre to your partner or group chat.</p>
          <div className="inline-block">
            <ShareFayreButton fayreId={fayre.id} fayreName={fayre.name} variant="inline" />
          </div>
        </div>

        {/* Final bottom CTA */}
        <div className="mt-4 rounded-2xl p-5 text-center" style={{ background: 'linear-gradient(135deg, rgba(108,85,224,0.18), rgba(194,157,82,0.08))', border: '1px solid rgba(194,157,82,0.25)' }} data-testid="fayre-final-cta">
          <p className="text-base font-black text-white mb-1">Ready to check availability?</p>
          <p className="text-white/55 text-xs mb-3">{filteredSuppliers.length} supplier{filteredSuppliers.length === 1 ? '' : 's'} attending — message before the day.</p>
          <button
            onClick={() => {
              const el = document.querySelector('[data-testid="fayre-category-pills"]');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="inline-flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-black text-white"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="fayre-final-cta-btn"
          >
            <ChatCircle size={14} weight="fill" /> Message suppliers attending
          </button>
        </div>
      </div>

      {/* Multi-message modal */}
      {msgModalOpen && (
        <FayreMultiMessageModal
          fayre={fayre}
          suppliers={filteredSuppliers.filter((s) => selectedIds.includes(s.id))}
          onClose={() => setMsgModalOpen(false)}
          onSent={(chatIds) => {
            setMsgModalOpen(false);
            setSelectedIds([]);
            toast.success(`Sent to ${chatIds.length} supplier${chatIds.length === 1 ? '' : 's'} 🎉`);
            navigate('/messages');
          }}
        />
      )}
    </div>
  );
};

export default FayreDetailPage;

// ── Category filter pill ─────────────────────────────────────────────
const CategoryPill = ({ active, onClick, label, testid }) => (
  <button
    onClick={onClick}
    className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-black border transition-all ${
      active
        ? 'bg-[#B89762]/40/20 text-[#F4E9D2] border-[#B89762]/45/50'
        : 'bg-white/[0.04] text-white/60 border-white/10 hover:bg-white/[0.08]'
    }`}
    data-testid={testid}
  >
    {label}
  </button>
);

// ── Supplier card inside the fayre (conversion surface) ──────────────
const FayreSupplierCard = ({ s, selected, onToggleSelect, onMessage, onView }) => {
  const ps = s.profile_signals || {};
  const replyCopy = ps.reply_speed_bucket ? REPLY_BUCKET_SHORT[ps.reply_speed_bucket] : null;
  const priceLabel = formatPrice(ps.min_price);
  const tierMeta = TIER_META[s.tier] || null;

  return (
    <div
      className={`rounded-xl p-3 border transition-all ${
        selected
          ? 'bg-[#B89762]/40/10 border-[#B89762]/45/40 ring-1 ring-[#B89762]/40'
          : 'bg-white/[0.03] hover:bg-white/[0.06] border-white/10 hover:border-white/20'
      }`}
      data-testid={`fayre-supplier-card-${s.id}`}
    >
      <div className="flex gap-3">
        {/* Avatar / image */}
        <button onClick={onView} className="shrink-0" data-testid={`fayre-supplier-view-${s.id}`}>
          <div
            className="w-20 h-20 rounded-lg bg-cover bg-center"
            style={{
              background: s.images?.[0]
                ? `url(${s.images[0]}) center/cover`
                : 'linear-gradient(135deg, rgba(108,85,224,0.3), rgba(194,157,82,0.2))',
            }}
          >
            {!s.images?.[0] && (
              <div className="w-full h-full flex items-center justify-center text-white/30 text-2xl">
                {CATEGORY_EMOJI[s.category] || '🎯'}
              </div>
            )}
          </div>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <button onClick={onView} className="text-left flex-1 min-w-0">
              <p className="font-bold text-sm leading-tight truncate">{s.business_name}</p>
              <p className="text-[10px] text-white/40 mt-0.5 truncate">
                {CATEGORY_EMOJI[s.category] || '🎯'} {CATEGORY_LABEL[s.category] || s.category} · {s.city || s.postcode}
              </p>
            </button>
            {tierMeta && (
              <span
                className={`text-[9px] font-black px-1.5 py-0.5 rounded-full border ${tierMeta.cls}`}
                data-testid={`fayre-tier-${s.id}`}
              >
                {tierMeta.label}
              </span>
            )}
          </div>
          {/* Trust chip row */}
          <div className="flex flex-wrap gap-1 mt-1.5">
            {s.is_verified && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-emerald-400/15 border border-emerald-400/40 text-emerald-200"
                data-testid={`fayre-card-verified-${s.id}`}
              >
                <CheckCircle size={9} weight="fill" /> Verified
              </span>
            )}
            {replyCopy && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#B89762]/40/10 text-[#C29D52]/85 border border-[#B89762]/45/25"
                data-testid={`fayre-card-reply-${s.id}`}
              >
                <Lightning size={9} weight="fill" /> {replyCopy}
              </span>
            )}
            {ps.has_certificates && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-[#B89762]/30/5 text-[#C29D52]/85 border border-[#B89762]/40/25"
                data-testid={`fayre-card-docs-${s.id}`}
              >
                <FileText size={9} weight="fill" /> Docs ready
              </span>
            )}
            {ps.bookings_this_week > 0 && (
              <span
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-400/10 text-amber-200 border border-amber-400/25"
                data-testid={`fayre-card-bookings-${s.id}`}
              >
                🔥 {ps.bookings_this_week}/wk
              </span>
            )}
          </div>
          {priceLabel && (
            <p className="text-[11px] text-white/70 font-bold mt-2">
              <span className="text-white/40 font-bold">From</span> {priceLabel}
            </p>
          )}
          {/* Actions */}
          <div className="flex gap-2 mt-2.5">
            <button
              onClick={onMessage}
              className="flex-1 py-2 rounded-lg text-[11px] font-bold text-white flex items-center justify-center gap-1"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid={`fayre-card-msg-${s.id}`}
            >
              <ChatCircle size={12} weight="fill" /> Message
            </button>
            <button
              onClick={onToggleSelect}
              className={`px-3 py-2 rounded-lg text-[11px] font-bold border ${
                selected
                  ? 'bg-[#B89762]/40/20 text-[#F4E9D2] border-[#B89762]/45/50'
                  : 'bg-white/[0.04] text-white/60 border-white/10 hover:bg-white/[0.08]'
              }`}
              data-testid={`fayre-card-select-${s.id}`}
              aria-pressed={selected}
            >
              {selected ? '✓' : '+'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Multi-supplier message modal (the "tap 3 suppliers" moment) ──────
const FayreMultiMessageModal = ({ fayre, suppliers, onClose, onSent }) => {
  const [message, setMessage] = useState(
    `Hi — I saw you at the ${fayre.name}. Are you available for my date?`
  );
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!message.trim()) { toast.error('Please write a message'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/fayres/${fayre.id}/message-many`, {
        supplier_ids: suppliers.map((s) => s.id),
        message: message.trim(),
      });
      onSent(data.sent_to || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-6"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="fayre-multimsg-modal"
    >
      <div className="w-full sm:max-w-md bg-[#15161B] border border-white/10 sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 pt-5 pb-3 border-b border-white/5 flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-wider font-black text-[#C29D52]/85/80">Message {suppliers.length} supplier{suppliers.length === 1 ? '' : 's'}</p>
            <h3 className="font-black text-lg mt-0.5">Fayre context attached</h3>
            <p className="text-white/50 text-xs mt-0.5">We'll tag each chat so the supplier knows this came from {fayre.name}.</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white -mr-2 p-2" data-testid="fayre-multimsg-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-white/40 mb-1.5">Recipients</label>
            <div className="flex flex-wrap gap-1.5">
              {suppliers.map((s) => (
                <span
                  key={s.id}
                  className="text-[11px] px-2 py-1 rounded-full bg-white/[0.06] text-white/80 border border-white/10"
                >
                  {s.business_name}
                </span>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-black uppercase tracking-wider text-white/40 mb-1.5">Your message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, 500))}
              rows={4}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#B89762]/40/40 resize-none"
              data-testid="fayre-multimsg-text"
            />
            <p className="text-[10px] text-white/30 mt-1">{message.length}/500</p>
          </div>
        </div>
        <div className="border-t border-white/5 px-5 py-3 flex gap-2">
          <button
            onClick={submit}
            disabled={busy}
            className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="fayre-multimsg-send"
          >
            <ChatCircle size={14} weight="fill" />
            {busy ? 'Sending…' : `Send to ${suppliers.length}`}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-bold bg-white/5 border border-white/10 hover:bg-white/10"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
